import { useId, useState, type ReactNode } from 'react';
import './FaqSection.css';

type FaqItem = {
  id: string;
  question: string;
  answer: ReactNode;
};

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'who',
    question: 'Who is Review Lumen for?',
    answer:
      'Review Lumen is built for local businesses that want more 5-star reviews without adding more work to their day. Whether you run a home service business, clinic, gym, rental company, restaurant, or multiple locations, our system helps you consistently turn happy customers into real reviews. Whether you’re a solo business owner or managing a growing team, Review Lumen is designed to make review generation simple and automated.',
  },
  {
    id: 'crm',
    question: 'Do you integrate with my CRM?',
    answer:
      'Yes - we integrate with many of the most popular CRMs, scheduling tools, and invoicing platforms businesses already use. That includes platforms like Jobber, QuickBooks Online, Housecall Pro, Workiz, Square, and thousands more through Zapier. If your business already uses software to manage customers, there’s a good chance we can connect with it.',
  },
  {
    id: 'reviews-time',
    question: 'How long does it take to start getting reviews?',
    answer:
      'Most businesses start seeing new reviews within the first 24–48 hours after launch. Results depend on the size of your customer list and how engaged your customers are, but our system is designed to get review requests out quickly and consistently.',
  },
  {
    id: 'billing',
    question: 'When do you bill?',
    answer:
      'Your subscription starts after your 14-day free trial ends, then renews automatically each month. Cancel anytime during the trial and you won’t be charged.',
  },
  {
    id: 'contract',
    question: 'Is there a contract?',
    answer:
      'No long-term contracts or commitments. Stay as long as you’re getting value, and cancel anytime directly from your dashboard.',
  },
  {
    id: 'pricing-tiers',
    question: 'How do the pricing tiers differ?',
    answer: (
      <>
        <p>
          Plans are based on how many review requests you want to send each month.
          Smaller businesses with fewer monthly customers can stay on lower
          plans, while larger teams can scale up as needed.
        </p>
        <p>
          Every plan also includes review reactivation campaigns and automated
          follow-ups to help maximize review volume.
        </p>
      </>
    ),
  },
  {
    id: 'bad-reviews',
    question: 'Can you keep bad reviews from Google?',
    answer: (
      <>
        <p>
          No - and no reputable platform should promise that. Google doesn’t allow
          businesses to hide or filter reviews.
        </p>
        <p>
          What we do instead is help you consistently generate more positive
          reviews from satisfied customers, improving your overall reputation
          naturally and safely.
        </p>
      </>
    ),
  },
  {
    id: 'frequency',
    question: 'How often do you ask my customers to leave a review?',
    answer: (
      <>
        <p>
          Most businesses use 1 review request followed by a couple of friendly
          reminders. Everything is customizable, so you control how often
          customers are contacted.
        </p>
        <p>
          We only send requests after service or during reactivation campaigns -
          never nonstop spam.
        </p>
      </>
    ),
  },
  {
    id: 'personalized-image',
    question: 'What is a personalized image review request?',
    answer:
      'Instead of sending generic text messages, we can send branded image-based review requests featuring your business, team, or customer name. These personalized requests grab attention and typically increase review response rates significantly.',
  },
  {
    id: 'expect-reviews',
    question: 'How many reviews should I expect?',
    answer:
      'Results vary by industry and customer base, but many businesses see 10–15% of past customers leave a review during reactivation campaigns. New customers often convert at higher rates after service.',
  },
  {
    id: 'reactivation',
    question: 'How does the review reactivation work?',
    answer:
      'We help you reconnect with previous customers by gradually sending personalized review requests over time. This creates a natural flow of new reviews while helping you get more value from your existing customer list.',
  },
  {
    id: 'other-platforms',
    question: 'What about reviews on Yelp, Facebook, and other platforms?',
    answer: (
      <>
        <p>
          Our main focus right now is Google reviews because they have the
          biggest impact on local visibility and customer trust.
        </p>
        <p>Additional platforms are being added over time.</p>
      </>
    ),
  },
  {
    id: 'countries',
    question: 'What countries do you currently support?',
    answer:
      'Currently, we support businesses in the United States, Canada, and the United Kingdom.',
  },
];

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <span
      className={`faq__chevron ${open ? 'faq__chevron--open' : ''}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <path d="M16 14.5a1 1 0 0 1-.71-.29L12 10.9l-3.3 3.18a1 1 0 0 1-1.41 0 1 1 0 0 1 0-1.42l4-3.86a1 1 0 0 1 1.4 0l4 4a1 1 0 0 1 0 1.42 1 1 0 0 1-.69.28z" />
      </svg>
    </span>
  );
}

function FaqAccordionItem({
  item,
  open,
  onToggle,
}: {
  item: FaqItem;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = useId();
  const triggerId = useId();

  return (
    <div className={`faq__item ${open ? 'faq__item--open' : ''}`}>
      <button
        type="button"
        id={triggerId}
        className="faq__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <h3 className="faq__question">{item.question}</h3>
        <ChevronIcon open={open} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className="faq__panel"
        hidden={!open}
      >
        <div className="faq__answer">{item.answer}</div>
      </div>
    </div>
  );
}

export function FaqSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <section className="faq" aria-labelledby="faq-heading">
      <div className="container faq__inner">
        <div className="faq__intro">
          <h2 id="faq-heading" className="faq__title">
            Frequently asked questions
          </h2>
        </div>
        <div className="faq__accordion">
          {FAQ_ITEMS.map((item) => (
            <FaqAccordionItem
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => handleToggle(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
